import { faInfoCircle as solidInfoCircle } from "@jewlr/pro-solid-svg-icons/faInfoCircle"
import { Box, Divider } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import { AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import React, { Suspense, useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { Footnote, FootnoteText } from "areas/product/jewlr/components"
import ImageCropper from "areas/product/jewlr/image-cropper"
import { TipText } from "areas/product/jewlr/modals/styled-components"
import PhotoUpload from "areas/product/jewlr/photo-upload"
import { updateChoice } from "areas/product/store/actions"
import { SlideContainer, MainContainer } from "components/HalfModal/slides"
import FontAwesomeIcon from "components/font-awesome-icon"
import Modal from "components/modal"
import { retry, jewlr_t } from "helpers/application"
import StorageHelper from "helpers/local-storage-cache"
import useWindowDimensions from "helpers/window-dimensions"

const PhotoTips = loadable(() => retry(() => import("./photo_tips")), {
  ssr: false,
})

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin: ${(props) => (props.margin ? props.margin : "")};
  & > div {
    display: flex;
    flex-direction: column;
  }
  margin-top: -1px;
  text-align: center;
  width: 100%;
`
const ImageCroperContainer = styled(Container)`
  ${(props) => props.theme.mediaQueries.tablet`
    & > div {
      flex-direction: row;
    }
  `}
`

const Index = {
  Upload: 0,
  // eslint-disable-next-line sort-keys
  Edit: 1,
  Tips: 2,
  TipsWithImgData: 3,
}

const InternalPhotoUploadModal = ({ pref, wizardData, routerHash }) => {
  const [contentIndex, setIndex] = useState(Index.Upload)
  const [cropperData, setCropperData] = useState({})
  const contentRef = useRef()
  const handwritingEngraving = pref && wizardData[pref]?.handwriting_engraving
  const photoLocket = pref && wizardData[pref]?.photo_locket
  const { isMobile } = useWindowDimensions()
  const isTipsIndex =
    contentIndex === Index.Tips || contentIndex == Index.TipsWithImgData

  useEffect(() => {
    let showTips = false
    if (routerHash.includes("#pendant-upload")) {
      const routerHashStack = routerHash.split("-")
      if (routerHash.includes("tips")) {
        showTips = true
        routerHashStack.pop()
      }
    }
    const cropper = wizardData && wizardData[pref] && wizardData[pref].cropper
    const key = `image-${pref}-${cropper?.aspect_ratio_width}-${cropper?.aspect_ratio_height}`
    if (wizardData && wizardData[pref] && !wizardData[pref].disabled) {
      if (StorageHelper.exist(key.trim())) {
        setIndex(showTips ? Index.TipsWithImgData : Index.Edit)
      } else {
        setIndex(showTips ? Index.Tips : Index.Upload)
      }
    }
    return () => {
      if (contentIndex == Index.Tips || contentIndex == Index.TipsWithImgData) {
        if (StorageHelper.exist(key.trim())) {
          setIndex(Index.Edit)
        } else {
          setIndex(Index.Upload)
        }
      }
    }
  }, [pref, routerHash])

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [contentIndex])

  const content = (
    <AnimatePresence>
      <Suspense fallback={<div></div>} key="photo-tips-suspense">
        {isTipsIndex && (
          <SlideContainer key="photo-tips">
            <PhotoTips pref={pref} />
          </SlideContainer>
        )}
      </Suspense>
      {contentIndex == Index.Edit && (
        <MainContainer animate="show" key="crop" width="90%">
          <ImageCroperContainer>
            <ImageCropper
              cropperData={cropperData[pref]}
              onMobileCallback={() => {
                setIndex(Index.Upload)
              }}
              onTipClick={() => {
                setIndex(Index.TipsWithImgData)
              }}
              pref={pref}
              setCropperData={(data) => {
                setCropperData({
                  ...cropperData,
                  [pref]: data,
                })
              }}
            />
          </ImageCroperContainer>
        </MainContainer>
      )}
      {contentIndex == Index.Upload && (
        <MainContainer
          animate={contentIndex != Index.Upload ? "hidden" : "show"}
          key="upload"
        >
          <Container>
            <div style={{ width: "100%" }}>
              <PhotoUpload
                onUpload={() => {
                  setIndex(Index.Edit)
                  setCropperData({
                    ...cropperData,
                    [pref]: {},
                  })
                }}
                pref={pref}
              />
            </div>
          </Container>
          <Footnote>
            <FootnoteText>
              Photo or image uploads that include inappropriate, explicit, or
              illegal content will not be accepted.
            </FootnoteText>
          </Footnote>
          <Box mb={1} pt="12px">
            <TipText onClick={() => setIndex(Index.Tips)}>
              <FontAwesomeIcon icon={solidInfoCircle} />
              <span>Read our tips for best results</span>
            </TipText>
          </Box>
        </MainContainer>
      )}
    </AnimatePresence>
  )

  let labelText = "Photo Upload"
  if (handwritingEngraving) {
    labelText = isTipsIndex ? "Handwriting Image Tips" : "Image Upload"
  } else if (photoLocket && isTipsIndex) {
    labelText = `${jewlr_t("terms.Personalized")} Photo Tips`
  }

  return (
    <Modal
      contentIndex={contentIndex}
      contentStyle={{
        px: { _: 2, tablet: 3 },
        ref: contentRef,
      }}
      headerStyle={{
        alignItems: "flex-start",
        mb: 0,
        pb: 2,
      }}
      height={contentIndex === 0 ? "fit-content" : "100%"}
      label={labelText}
      labelStyle={{
        flex: 1,
        mt: 3,
      }}
      onBack={() => {
        if (contentIndex == Index.TipsWithImgData) {
          setIndex(Index.Edit)
        } else if (contentIndex == Index.Tips) {
          setIndex(Index.Upload)
        }
      }}
      show={
        pref === "box"
          ? "#photo-upload"
          : [`#pendant-upload-${pref}`, `#pendant-upload-${pref}-tips`]
      }
      variant={!isMobile || contentIndex === 0 ? "squircle" : "base"}
      width={
        contentIndex == Index.Upload
          ? "482px"
          : contentIndex == Index.Edit
          ? "812px"
          : "612px"
      }
      withBackButton={contentIndex !== Index.Edit}
    >
      {!isMobile && <Divider bg="grey.10" mt={-2} />}
      {content}
    </Modal>
  )
}

InternalPhotoUploadModal.propTypes = {
  pref: PropTypes.string,
  routerHash: PropTypes.string,
  setChoice: PropTypes.func,
  wizardData: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    routerHash: state.router.location.hash,
    wizardData: state.wizard.data,
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data))
    },
  }
}

const PhotoUploadModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalPhotoUploadModal)

export default PhotoUploadModal
